import debounce from "lodash/debounce";
import React, { useEffect, useRef, useState } from "react";

import { FullbleedMediaHeroBlock } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ButtonPrimary } from "../Clickables";
import { FullbleedMedia } from "../FullbleedMedia";
import { RichText } from "../RichText";
import { getThemeClass } from "../ThemedModule";

import styles from "./index.module.scss";

interface Props {
    value: FullbleedMediaHeroBlock;
}

export const FullbleedMediaHero = ({ value }: Props) => {
    const FEATURED_CARD_INDEX = 2; // The card that expands (third card, zero-indexed)
    const SCROLL_DISTANCE = 900; // Total scroll distance in pixels for the animation

    const gridContainerRef = useRef<HTMLDivElement>(null);
    const featuredCardRef = useRef<HTMLDivElement>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [videoAutoplay, setVideoAutoplay] = useState(false);
    const scrollPositionRef = useRef(0);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        // Once animating, the callback's !isAnimating guard makes any new
        // observer a no-op — skip the setup until isAnimating flips back.
        if (isAnimating) return;

        const wrapper = gridContainerRef.current?.parentElement;
        if (!wrapper || !featuredCardRef.current) return;

        let observer: IntersectionObserver | null = null;

        // Observe the wrapper rather than the card: the card's sticky pin
        // sits exactly on the IO trigger line, so it never crosses cleanly.
        // Rebuilt on resize since rootMargin is in pixels.
        const setupObserver = () => {
            observer?.disconnect();
            if (!featuredCardRef.current) return;

            const vh = window.innerHeight;
            const elementHeight = featuredCardRef.current.offsetHeight;
            const topMargin = -vh * 0.5 + elementHeight * 0.5;
            const bottomMargin = -vh * 0.5 - elementHeight * 0.5;

            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setIsAnimating(true);
                            setVideoAutoplay(true);
                            if (!featuredCardRef.current) return;

                            // Use the natural animation start (scroll position at which
                            // sticky activates), not the current scrollY. On refresh
                            // mid-animation, window.scrollY is the restored position,
                            // which would make progress calculate to 0 and snap the
                            // card back to its initial state.
                            const wrapperTop =
                                wrapper.getBoundingClientRect().top +
                                window.scrollY;
                            const cardHeight =
                                featuredCardRef.current.offsetHeight;
                            const stickyTopOffset =
                                (window.innerHeight - cardHeight) / 2;
                            scrollPositionRef.current =
                                wrapperTop - stickyTopOffset;
                        }
                    });
                },
                {
                    root: null,
                    rootMargin: `${topMargin}px 0px ${bottomMargin}px 0px`,
                    threshold: 0,
                },
            );

            observer.observe(wrapper);
        };

        setupObserver();

        const debouncedSetup = debounce(setupObserver, 150);
        window.addEventListener("resize", debouncedSetup);

        return () => {
            observer?.disconnect();
            debouncedSetup.cancel();
            window.removeEventListener("resize", debouncedSetup);
        };
    }, [isAnimating]);

    // Handle scroll to expand the featured card width
    useEffect(() => {
        if (!isAnimating) return;

        const updateAnimation = () => {
            if (rafRef.current) return;

            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;

                const currentScroll = window.scrollY;
                const startScroll = scrollPositionRef.current;

                if (currentScroll < startScroll) {
                    if (gridContainerRef.current) {
                        gridContainerRef.current.style.setProperty(
                            "--size-progress",
                            "0",
                        );
                        gridContainerRef.current.style.setProperty(
                            "--effect-progress",
                            "0",
                        );
                        gridContainerRef.current.removeAttribute(
                            "data-fullscreen",
                        );
                    }

                    setIsAnimating(false);
                    return;
                }

                const scrollProgress =
                    (currentScroll - startScroll) / SCROLL_DISTANCE;
                const clampedProgress = Math.max(
                    0,
                    Math.min(1, scrollProgress),
                );

                const sizeProgress = Math.min(1, clampedProgress / 0.75);
                const effectProgress = Math.max(
                    0,
                    (clampedProgress - 0.75) * 4,
                );

                if (gridContainerRef.current) {
                    gridContainerRef.current.style.setProperty(
                        "--size-progress",
                        `${sizeProgress}`,
                    );
                    gridContainerRef.current.style.setProperty(
                        "--effect-progress",
                        `${effectProgress}`,
                    );
                    gridContainerRef.current.toggleAttribute(
                        "data-fullscreen",
                        sizeProgress >= 1,
                    );
                }
            });
        };

        window.addEventListener("scroll", updateAnimation, { passive: true });

        // Sync CSS vars to current scroll position immediately. Without this,
        // a refresh mid-animation would show the card at progress 0 until the
        // next scroll event.
        updateAnimation();

        return () => {
            window.removeEventListener("scroll", updateAnimation);

            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [isAnimating]);

    const gridItems = value.images
        ? [
              ...value.images.slice(0, FEATURED_CARD_INDEX),
              null, // Placeholder for featured card
              ...value.images.slice(FEATURED_CARD_INDEX),
          ]
        : [];

    return (
        <section
            className={concatClassNames([
                styles.root,
                getThemeClass(value.module_theme),
            ])}
            style={{
                "--scroll-distance": `${SCROLL_DISTANCE}px`,
            }}
        >
            <div className={styles.content}>
                {value.title && (
                    <RichText value={value.title} className={styles.title} />
                )}
                {value.subtitle && (
                    <RichText
                        value={value.subtitle}
                        className={styles.subtitle}
                    />
                )}
                {value.cta?.display_name && (
                    <ButtonPrimary
                        simpleLink={value.cta}
                        className={styles.cta}
                    />
                )}
            </div>

            <div className={styles.imageGridWrapper}>
                <div
                    className={`${styles.imageGridContainer} ${isAnimating ? styles.animating : ""}`}
                    ref={gridContainerRef}
                >
                    <div className={styles.imageGrid}>
                        {gridItems.map((item, index) => {
                            const isFeatured = index === FEATURED_CARD_INDEX;
                            const key = isFeatured
                                ? "featured"
                                : `item-${index}`;

                            return (
                                <div
                                    key={key}
                                    className={`${styles.gridTile} ${isFeatured ? styles.featuredCard : styles.otherCard}`}
                                    ref={isFeatured ? featuredCardRef : null}
                                >
                                    {isFeatured && value.featured_card ? (
                                        <FullbleedMedia
                                            value={value.featured_card}
                                            controlled
                                            autoplay={videoAutoplay}
                                        />
                                    ) : item ? (
                                        <div className={styles.mediaWrapper}>
                                            <ImageChooserBlock
                                                value={item.value}
                                                attrs={{}}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
